import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, Observable, Subscription, tap } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import { GridEventService } from '../grid/services/grid-event.service';
import {
  ExDashboardService,
  GetDashboardResponse,
} from './ex-dashboard.service';
import { Dashboard } from './models/Dashboard';
import { DashboardAction } from './models/DashboardAction';
import { DashboardComponent } from './models/DashboardComponent';
import { DashboardFilters } from './models/DashboardFilters';
import { DashboardOptions } from './models/DashboardOptions';
import * as _ from 'lodash-es';
import { isString } from 'lodash-es';
import { HorizontalBusinessGroupNode } from './models/HorizontalBusinessGroupNode';
import { Constants } from './global-defaults/Constants';
import { ExDashboardEventService } from './services/event.service';
import { RawDataDialogComponent } from './components/raw-data-dialog/raw-data-dialog.component';
import { EditComponentDialogComponent, EditComponentDialogData } from './components/edit-component-dialog/edit-component-dialog.component';

import { safeJsonInput } from '../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-ex-dashboard',
    templateUrl: './ex-dashboard.component.html',
    styleUrls: ['./ex-dashboard.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ExDashboardComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  //flags
  dashboardLayoutLoading: boolean = false;
  isSheetCache: boolean = false;
  dashboardLoaded: boolean = false;
  filtersSelected: boolean = false;
  isFiltering: boolean = false;
  currentTheme: string = 'light';

  //data objects
  dashboardId: string = '';
  dashboard!: Dashboard;
  activeScreen: any;
  private loadingScreen: any; // reference to the screen that initiated loading
  dashboardOptions: DashboardOptions = {
    dashboard_alert_text: '',
    dashboard_desc: '',
    dashboard_id: '',
    dashboard_key: '',
    dashboard_name: '',
    dashboard_type: '',
    components: [],
    data_sheets: [],
    filters: []
  };
  filters!: DashboardFilters[];
  footerFiltersLists: any[] = [];
  actions: DashboardAction[] = [];
  /** Filter definitions from `/getDashboardByKey` — kept separate from runtime `dashboard.filters`. */
  private dashboardConfigFilters: DashboardFilters[] = [];

  //observables
  private $fetchDashboardOptions!: Observable<DashboardOptions>;
  private fetchDashboardSub?: Subscription;
  private tabSub!: Subscription;
  meSelectedListener!: Subscription;
  loadListener!: Subscription;
  sheetLoadedListeners: Record<string, Subscription> = {};

  constructor(
    private dashboardService: ExDashboardService,
    private workspaceService: WorkspaceService,
    private appService: AppService,
    private eventService: ExDashboardEventService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private hostRef: ElementRef<HTMLElement>,
  ) {
    this.activeScreen = this.workspaceService.workspaceFactory.activeScreen;
  }

  ngOnInit(): void {
    console.log('[ExDashboard] ngOnInit, activeScreen:', this.activeScreen?.screen_key, 'link:', this.activeScreen?.link);
    // Subscribe to tab activation (triggered by DataFiltersComponent.refresh())
    this.tabSub = this.workspaceService.setTabActive.subscribe((ws: any) => {
      console.log('[ExDashboard] setTabActive received:', ws?.screen_key, 'link:', ws?.link, 'filters.loaded:', ws?.filters?.loaded);
      if (!ws?.screen_key) return;
      if (!ws.filters?.loaded) return;

      const routeConfig = this.appService.ROUTES?.[ws.link];
      console.log('[ExDashboard] routeConfig:', routeConfig?.type, 'component:', routeConfig?.component);
      if (routeConfig?.type !== 'dashboard') return;

      this.activeScreen = ws;
      this.refreshGrid({});
    });
  }

  private resolveDashboardId(): void {
    const routeConfig = this.appService.ROUTES?.[this.activeScreen?.link];
    const apiGet = routeConfig?.api?.get || '';
    // apiGet = "getDashboardByKey?dashboard_id=0mwy6c"
    if (apiGet.includes('dashboard_id=')) {
      this.dashboardId = apiGet.split('dashboard_id=')[1];
    }
  }

  /** Cancels a pending viewport measurement when the dashboard is destroyed. */
  private viewportSyncRaf = 0;

  ngAfterViewInit(): void {
    this.meSelectedListener = this.eventService.on('dashboard:meSelected').subscribe((event: { node: HorizontalBusinessGroupNode; cntrlKey: boolean }) => this.meSelected(event.node, event.cntrlKey));
    this.loadListener = this.eventService.on('dashboard:load').subscribe((event: any) => this.refreshGrid(event));
    this.eventService.broadcast('dashboard:events-loaded', {});
    this.scheduleViewportSync();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleViewportSync();
  }

  /**
   * Size the host to the space actually left on screen. A fixed
   * `100vh - 182px` box is taller than the pane under the tabs and filters,
   * so its scrollbar sits below the fold and later cards paint over each other.
   */
  private scheduleViewportSync(): void {
    if (this.viewportSyncRaf) {
      cancelAnimationFrame(this.viewportSyncRaf);
    }
    this.viewportSyncRaf = requestAnimationFrame(() => {
      this.viewportSyncRaf = 0;
      this.syncViewportHeight();
    });
  }

  private syncViewportHeight(): void {
    const host = this.hostRef.nativeElement;
    if (!host.isConnected) {
      return;
    }
    const top = host.getBoundingClientRect().top;
    const available = Math.floor(window.innerHeight - top - 8);
    if (available < 160) {
      return;
    }
    host.style.height = `${available}px`;
  }

  refreshGrid(event: any) {
    this.dashboardLoaded = false;
    this.filtersSelected = false;
    this.filters = [];
    this.footerFiltersLists = [];
    // Clean up old sheet listeners
    Object.values(this.sheetLoadedListeners).forEach(sub => sub.unsubscribe());
    this.sheetLoadedListeners = {};
    this.resolveDashboardId();
    console.log('[ExDashboard] refreshGrid called, dashboardId:', this.dashboardId, 'activeScreen:', this.activeScreen?.link);

    if (!this.dashboardId) {
      console.error('[ExDashboard] No dashboardId resolved');
      return;
    }

    this.loadingScreen = this.activeScreen;
    this.dashboardLayoutLoading = true;
    this.activeScreen.loading_data = true;

    if (this.activeScreen && this.activeScreen.data) {
      const cachedId = this.activeScreen.data.dashboard_id || this.activeScreen.data.id;
      if (cachedId && cachedId !== this.dashboardId) {
        // Stale cache from a different dashboard - clear it
        console.log('[ExDashboard] Clearing stale cache:', cachedId, 'expected:', this.dashboardId);
        this.activeScreen.data = null;
      } else {
        this.dashboardOptions.dashboard_name = this.activeScreen.data.dashboard_name || this.activeScreen.data.name;
        this.dashboardOptions.dashboard_id = this.activeScreen.data.dashboard_id || this.activeScreen.data.id;
        this.dashboardOptions.data_sheets = this.activeScreen.data.data_sheets || this.activeScreen.data.dataSheets;
        this.dashboardOptions.components = this.activeScreen.data.components || this.activeScreen.data.components;
        this.dashboardConfigFilters = this.normalizeConfigFilters(
          this.activeScreen.data.configFilters ?? this.activeScreen.data.filters
        );
        this.dashboardOptions.filters = this.dashboardConfigFilters;
        this.isSheetCache = true;

        this.activeScreen.data.firstLoad = 0;
        this.dashboardLayoutLoading = false;
        this.initDashboard();
        return;
      }
    }

    console.log('[ExDashboard] Fetching dashboard config for:', this.dashboardId);
    this.$fetchDashboardOptions = this.dashboardService
      .loadDashboardObservable(this.dashboardId)
      .pipe(
        map((response) => {
          console.log('[ExDashboard] Dashboard API response received, components rows:', response?.dashboard?.components?.length);
          return response.dashboard;
        }),
        tap({
          next: (dashboard: DashboardOptions) => {
            console.log('[ExDashboard] Dashboard loaded:', dashboard?.dashboard_name, 'sheets:', dashboard?.data_sheets?.length, 'components:', dashboard?.components?.length);
            this.dashboardOptions = dashboard;
            this.dashboardConfigFilters = this.normalizeConfigFilters(dashboard.filters);
            this.dashboardOptions.filters = this.dashboardConfigFilters;
            this.dashboardLayoutLoading = false;

            this.workspaceService.setCache(
              this.loadingScreen || this.activeScreen,
              {
                ...this.dashboardOptions,
                configFilters: this.dashboardConfigFilters,
              }
            );
            this.workspaceService.checkFilters(this.loadingScreen || this.activeScreen);

            this.isSheetCache = false;

            this.initDashboard();
          },
        })
      );

    // Cancel any in-flight fetch from a previous refreshGrid before
    // starting a new one - rapid filter / tab toggling otherwise stacks
    // overlapping dashboard config requests whose late callbacks would
    // overwrite the freshly initialised dashboard.
    this.fetchDashboardSub?.unsubscribe();
    this.fetchDashboardSub = this.$fetchDashboardOptions.subscribe();
  }

  ngOnDestroy(): void {
    if (this.viewportSyncRaf) {
      cancelAnimationFrame(this.viewportSyncRaf);
    }
    if (this.tabSub) this.tabSub.unsubscribe();
    if (this.meSelectedListener) this.meSelectedListener.unsubscribe();
    if (this.loadListener) this.loadListener.unsubscribe();
    this.fetchDashboardSub?.unsubscribe();

    this.dashboard?.dataSheets.forEach((sheet: any) => {
      if (this.sheetLoadedListeners[sheet.name]) {
        this.sheetLoadedListeners[sheet.name].unsubscribe();
      }
    });
  }

  private parseExtraOptions(): void {
    if (this.dashboardOptions?.components) {
      for (const row of this.dashboardOptions.components) {
        if (!Array.isArray(row)) continue;
        for (const comp of row) {
          if (comp.extraOptions && isString(comp.extraOptions)) {
            try {
              comp.extraOptions = safeJsonInput<any>(comp.extraOptions, null, 'ex-dashboard.parse1');
            } catch (e) {
              try {
                comp.extraOptions = JSON.parse(comp.extraOptions.replace(/'/g, '"'));
              } catch (e2) {
                console.warn('[ExDashboard] Could not parse extraOptions for component:', comp.component_id, e2);
              }
            }
          }
          // Also parse KPI extraOptions
          if (comp.extraOptions?.kpis) {
            for (const kpi of comp.extraOptions.kpis) {
              if (kpi.extraOptions && isString(kpi.extraOptions)) {
                try {
                  kpi.extraOptions = safeJsonInput<any>(kpi.extraOptions, null, 'ex-dashboard.parse2');
                } catch (e) {
                  try {
                    kpi.extraOptions = JSON.parse(kpi.extraOptions.replace(/'/g, '"'));
                  } catch (e2) {
                    console.warn('[ExDashboard] Could not parse KPI extraOptions:', kpi.component_id, e2);
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  initDashboard() {
    console.log('[ExDashboard] initDashboard called, isSheetCache:', this.isSheetCache, 'data_sheets:', this.dashboardOptions?.data_sheets?.length, 'components:', this.dashboardOptions?.components?.length);
    this.parseExtraOptions();
    const dataSheets = this.dashboardOptions?.data_sheets
      || (this.dashboardOptions as any)?.dataSheets
      || [];
    const components = this.dashboardOptions?.components || [];
    this.dashboard = !this.isSheetCache ? this.dashboardService.createOrUpdateDashboard(
      this.dashboardOptions.dashboard_name,
      this.dashboardOptions.dashboard_id,
      dataSheets,
      components
    ) : this.dashboardService.getDashboardById(this.dashboardOptions.dashboard_id);

    this.dashboardLoaded = this.dashboard.dataLoaded;
    this.filters = this.dashboard.getFilters();
    this.footerFiltersLists = this.dashboard.getFooterFilters();

    this.actions = [
      { title: 'Edit Component', icon: 'fa fa-edit', onClick: (component: DashboardComponent) => this.editComponentPopup(component), adminOnly: true },
      { title: 'View raw data', icon: 'fa fa-table', onClick: (component: DashboardComponent) => this.viewRawData(component) }
    ];

    const userType = this.appService.USER_SETTINGS?.user?.user_type;
    if (userType !== 'Super Admin') {
      this.actions = this.actions.filter(a => !a.adminOnly);
    }

    this.filtersSelected = true;
    var filterparams = this.activeScreen.filters
      ? this.activeScreen.filters.getFilterParams()
      : {};
    this.workspaceService.checkFilters(this.activeScreen);

    if (!this.isSheetCache) {

      if (this.dashboard.dataSheets.length === 0) {
        this.dashboardLoaded = true;
      }
      this.dashboard
        .loadSheets().pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (responses: any[]) => {
            console.log('[ExDashboard] Sheets loaded successfully, responses:', responses?.length);
            (this.loadingScreen || this.activeScreen).loading_data = false;
            this.dashboardLoaded = true;
            // Ensure all sheets are marked loaded (some may miss the flag due to race conditions)
            for (const sheet of this.dashboard.dataSheets) {
              sheet.isLoaded = true;
            }
            // Rebuild all filters now that ALL sheets are loaded (catches lost per-sheet events)
            for (const sheet of this.dashboard.dataSheets) {
              this.filterSetup(sheet.name);
            }
            this.cdr.detectChanges();
            this.scheduleViewportSync();
          },
          error: (err: any) => {
            console.error('[ExDashboard] Sheet loading FAILED:', err);
            (this.loadingScreen || this.activeScreen).loading_data = false;
            this.dashboardLoaded = true;
            this.scheduleViewportSync();
          }
        });

      for (var key in this.dashboard.dataSheets) {
        let sheetLoadedLisener = this.eventService.on(
          'sheet-' + this.dashboard.dataSheets[key].name + ':loaded').subscribe(
            (sheetName: string) => this.sheetLoaded(sheetName)
          );

        this.sheetLoadedListeners[this.dashboard.dataSheets[key].name] = sheetLoadedLisener;
      }
    } else {
      // Cache path: always rebuild filters from loaded sheet data
      for (var key in this.dashboard.dataSheets) {
        this.filterSetup(this.dashboard.dataSheets[key].name);
      }
    }
    this.cdr.detectChanges();
    this.scheduleViewportSync();
  }

  sheetLoaded(sheetName: string) {
    console.log('[ExDashboard] sheetLoaded event:', sheetName, 'all sheets loaded:', this.dashboard?.dataSheets?.map((s: any) => s.name + '=' + s.isLoaded));
    this.filterSetup(sheetName);
    console.log('[ExDashboard] filters after setup:', this.filters?.map((f: any) => f.name + ':values=' + f.values?.length));

    this.dashboardLoaded = true;
    this.scheduleViewportSync();
    this.workspaceService.setCache(
      this.loadingScreen || this.activeScreen,
      {
        ...this.dashboardOptions,
        configFilters: this.dashboardConfigFilters,
        dashboard_name: this.dashboard.name,
        dashboard_id: this.dashboard.id,
        data_sheets: this.dashboard.dataSheets,
        dataSheets: this.dashboard.dataSheets,
        components: this.dashboardOptions.components,
        filters: this.dashboardConfigFilters,
        firstLoad: 0,
      }
    );
    this.cdr.detectChanges();
  }

  /**
   * `/getDashboardByKey` may return `filters` as a JSON string, object map, or
   * runtime UI state after cache — normalize to an array before `.filter()`.
   */
  private normalizeConfigFilters(raw: unknown): DashboardFilters[] {
    if (raw == null) {
      return [];
    }
    let parsed: unknown = raw;
    if (typeof parsed === 'string') {
      try {
        parsed = safeJsonInput<unknown>(parsed, [], 'ex-dashboard.filters');
      } catch {
        return [];
      }
    }
    if (!Array.isArray(parsed)) {
      if (typeof parsed === 'object') {
        parsed = Object.values(parsed as Record<string, unknown>);
      } else {
        return [];
      }
    }
    return (parsed as any[])
      .filter((f) => f && typeof f === 'object')
      .map((f) => this.normalizeConfigFilter(f));
  }

  private normalizeConfigFilter(filter: DashboardFilters): DashboardFilters {
    return {
      ...filter,
      sheet: this.normalizeFilterSheet(filter.sheet),
      value: Array.isArray(filter.value) ? filter.value : [],
      values: Array.isArray(filter.values) ? filter.values : [],
      selected: Array.isArray(filter.selected) ? filter.selected : [],
    };
  }

  /** Backend may send `sheet` as string, JSON array string, or string[]. */
  private normalizeFilterSheet(sheet: unknown): string[] {
    if (Array.isArray(sheet)) {
      return sheet.map((s) => String(s));
    }
    if (typeof sheet === 'string') {
      const trimmed = sheet.trim();
      if (!trimmed) {
        return [];
      }
      if (trimmed.startsWith('[')) {
        try {
          const parsed = safeJsonInput<string[]>(trimmed, [], 'ex-dashboard.filter-sheet');
          if (Array.isArray(parsed)) {
            return parsed.map((s) => String(s));
          }
        } catch {
          /* fall through */
        }
      }
      return trimmed.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
    }
    return [];
  }

  filterChange() {
    this.isFiltering = true;
    this.cdr.detectChanges();

    // setTimeout allows DOM to paint the spinner before synchronous pipe() runs
    setTimeout(() => {
      for (var key in this.filters) {
        if (this.filters.hasOwnProperty(key)) {
          var filter = this.filters[key];
          var colSearch = this.dashboard.tableState.colSearch.find(function(
            item
          ) {
            return item.name == filter.name;
          });
          if (colSearch && filter.selected) colSearch.value = filter.selected;
          this.eventService.broadcast(
            'dashboard:filter:' + filter.name.toLowerCase() + '-changed',
            filter
          );
        }
      }
      this.dashboard.pipe();
      this.footerFiltersLists = [];
      for (var key in this.filters) {
        if (!Array.isArray(this.filters[key])) {
          if (this.filters[key].selected.length > 0) {
            this.filters[key].tooltip = this.filters[key].selected
              .map(function(item) {
                return item.LABEL;
              })
              .join(', ');
            this.footerFiltersLists.push(this.filters[key]);
          }
        }
      }
      this.dashboard.setFilters(this.filters);
      this.dashboard.setFooterFilters(this.footerFiltersLists);
      this.isFiltering = false;
      this.cdr.detectChanges();
    }, 50);
  }

  filterSetup(sheetName: string) {
    const configFilters = this.normalizeConfigFilters(this.dashboardOptions?.filters);
    if (!configFilters.length || !this.dashboard) {
      return;
    }

    const sheetFilters = configFilters.filter((filter) => {
      const sheets = this.normalizeFilterSheet(filter.sheet);
      return sheets.includes(sheetName);
    });

    if (!Array.isArray(this.dashboard.tableState.colSearch)) {
      this.dashboard.tableState.colSearch = [];
    }

    for (const filter of sheetFilters) {
      const filterTemplate: DashboardFilters = {
        ...filter,
        sheet: this.normalizeFilterSheet(filter.sheet),
        value: [],
      };
      const filterIndex = _.findIndex(this.filters, ['name', filterTemplate.name]);
      const built = this.buildFilter(filterTemplate);
      if (filterIndex === -1) {
        this.filters.push(built);
        console.log('pushed filter', filterTemplate.name);
      } else {
        this.filters[filterIndex] = built;
        console.log('updated filter', filterTemplate.name);
      }
      _.remove(this.dashboard.tableState.colSearch, ['name', filterTemplate.name]);
      this.dashboard.tableState.colSearch.push({
        ...filterTemplate,
        value: [],
      });
    }
    this.filters = [...this.filters];
  }

  buildFilter(filter: DashboardFilters) {
    const filterObj = { ...filter };
    filterObj.selected = [];
    filterObj.values = [];
    filterObj.name = filter.name;
    filterObj.label = filter.label || ('Select  ' + filter.name);
    filterObj.order = filter.order;
    filterObj.sheet = _.uniq(this.normalizeFilterSheet(filter.sheet));
    const filterparams = this.activeScreen.filters
      ? this.activeScreen.filters.getFilterParams()
      : {};

    const sheets = (this.dashboard?.dataSheets || []).filter((item) => {
      return filterObj.sheet.includes(item.name) && item.isLoaded;
    });

    console.log('[ExDashboard] buildFilter:', filter.name, 'loaded sheets:', sheets.length, '/', filterObj.sheet.length, 'filterparams:', filterparams);

    if (sheets.length !== filterObj.sheet.length) {
      return filterObj;
    }

    for (const sheet of sheets) {
      const rows = Array.isArray(sheet.data) ? sheet.data : [];
      const filterDataFromSheet: { LABEL: string; VALUE: string }[] = _.reject(
        _.sortBy(
          _.map(
            _.groupBy(
              _.filter(rows, (item: any) => {
                if (!item?.['TAX_YEAR'] || filterparams['tax_year'] == null) {
                  return true;
                }
                return item['TAX_YEAR'].toString() === filterparams['tax_year'].toString();
              }),
              filter.predicate
            ),
            (v, key) => {
              return {
                LABEL: key,
                VALUE: key,
              };
            }
          ),
          'NAME'
        ),
        ['VALUE', 'undefined']
      );

      filterObj.values = _.unionBy(
        filterObj.values,
        filterDataFromSheet,
        'VALUE'
      );
      console.log('[ExDashboard] buildFilter sheet:', sheet.name, 'predicate:', filter.predicate, 'filterDataFromSheet:', filterDataFromSheet.length, 'total values:', filterObj.values.length);
    }

    return filterObj;
  }

  clearFilters() {
    for (var key in this.filters) {
      if (this.filters.hasOwnProperty(key)) {
        var filter = this.filters[key];
        filter.model = [];
        filter.selected = [];
        // Create new reference to trigger ngOnChanges on dropdown component
        this.filters[key] = { ...filter };
      }
    }
    this.filters = [...this.filters];
    this.filterChange();
  }

  clearFilter(filter: DashboardFilters) {
    if (this.filters.length === 0) return;

    const filterIndex = this.filters.findIndex(function(item) {
      return item.name === filter.name;
    });
    if (filterIndex === -1) return;
    this.filters[filterIndex].model = [];
    this.filters[filterIndex].selected = [];
    // Create new reference to trigger ngOnChanges on dropdown component
    this.filters[filterIndex] = { ...this.filters[filterIndex] };
    this.filters = [...this.filters];
    this.filterChange();
  }

  viewRawData(component: DashboardComponent) {
    const sheet = this.dashboard.dataSheets.find(s => s.name === component.srcSheet);
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'modal-lg';
    dialogConfig.position = { top: '4vh' };
    dialogConfig.width = '80vw';
    dialogConfig.data = {
      title: component.title,
      data: sheet?.data || [],
      component_id: component.component_id
    };
    this.dialog.open(RawDataDialogComponent, dialogConfig);
  }

  editComponentPopup(component: DashboardComponent) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'modal-xl';
    dialogConfig.width = '92%';
    dialogConfig.data = {
      editObj: component,
      sheetsObj: this.dashboard.dataSheets,
      fullData: false,
      dashboard_id: this.dashboardOptions.dashboard_id,
      rowIndex: component.rowIndex,
      columns: component.columns
    } as EditComponentDialogData;

    const dialogRef = this.dialog.open(EditComponentDialogComponent, dialogConfig);
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: any) => {
      if (result) {
        // Update component in dashboard
        for (const row of this.dashboardOptions.components) {
          if (!Array.isArray(row)) continue;
          const idx = row.findIndex((c: any) => c.component_id === result.component_id);
          if (idx !== -1) {
            Object.assign(row[idx], result);
            this.eventService.broadcast('component-' + result.component_id + ':reload', result);
            break;
          }
        }
        this.cdr.detectChanges();
      }
    });
  }

  meSelected(node: HorizontalBusinessGroupNode, cntrlKeyDown: boolean) {
    var meFilter = this.filters.find(function(item) {
      return item.name === 'ME';
    })!;
    if (!cntrlKeyDown) {
      meFilter.selected = [];
    }
    if (meFilter != undefined) {
      if (_.findIndex(meFilter.selected, ["LABEL", node.name]) === -1) {
        meFilter.selected.push({
          LABEL: node.name,
          VALUE: node.name
        });
      } else {
        _.remove(meFilter.selected, (f => f.LABEL === node.name));
      }
    }
    this.filterChange();
  }
}
